import { useState, useEffect } from 'react';
import { QrCode, Copy, ExternalLink, CheckCircle } from 'lucide-react';

interface QRCodeVerificationProps {
  verificationCode: string;
  verificationUrl?: string;
  size?: 'sm' | 'md' | 'lg';
  showLink?: boolean;
  className?: string;
}

const SIZES = {
  sm: 128,
  md: 200,
  lg: 300,
};

export function QRCodeVerification({
  verificationCode,
  verificationUrl,
  size = 'md',
  showLink = true,
  className = '',
}: QRCodeVerificationProps) {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fullUrl = verificationUrl || `${window.location.origin}/verify/${verificationCode}`;
  const qrSize = SIZES[size];

  useEffect(() => {
    const generateQRCode = async () => {
      try {
        // Using a simple QR code generation via canvas
        // In production, you'd use a library like 'qrcode'
        const QRCode = await import('qrcode');
        const dataUrl = await QRCode.toDataURL(fullUrl, {
          width: qrSize,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff',
          },
        });
        setQrCodeDataUrl(dataUrl);
      } catch (err) {
        console.error('Error generating QR code:', err);
        setError('Erro ao gerar QR Code');
      }
    };

    generateQRCode();
  }, [fullUrl, qrSize]);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(verificationCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (error) {
    return (
      <div className={`flex flex-col items-center p-4 bg-gray-50 rounded-lg ${className}`}>
        <QrCode className="w-16 h-16 text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {/* QR Code */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        {qrCodeDataUrl ? (
          <img
            src={qrCodeDataUrl}
            alt="QR Code de Verificação"
            width={qrSize}
            height={qrSize}
            className="block"
          />
        ) : (
          <div
            className="flex items-center justify-center bg-gray-100 animate-pulse"
            style={{ width: qrSize, height: qrSize }}
          >
            <QrCode className="w-12 h-12 text-gray-300" />
          </div>
        )}
      </div>

      {/* Verification Code */}
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-500 mb-1">Código de Verificação</p>
        <div className="flex items-center gap-2">
          <code className="px-3 py-1 bg-gray-100 rounded font-mono text-sm">
            {verificationCode}
          </code>
          <button
            onClick={handleCopyCode}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Copiar código"
          >
            {copied ? (
              <CheckCircle className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4 text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* Verification Link */}
      {showLink && (
        <div className="mt-4 w-full max-w-xs">
          <p className="text-xs text-gray-500 mb-1 text-center">Link de Verificação</p>
          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
            <input
              type="text"
              value={fullUrl}
              readOnly
              className="flex-1 text-xs bg-transparent border-none outline-none text-gray-600 truncate"
            />
            <button
              onClick={handleCopyUrl}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
              title="Copiar link"
            >
              <Copy className="w-4 h-4 text-gray-400" />
            </button>
            <a
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 hover:bg-gray-200 rounded transition-colors"
              title="Abrir link"
            >
              <ExternalLink className="w-4 h-4 text-gray-400" />
            </a>
          </div>
        </div>
      )}

      {/* Instructions */}
      <p className="mt-4 text-xs text-gray-500 text-center max-w-xs">
        Escaneie o QR Code ou acesse o link para verificar a autenticidade deste documento.
      </p>
    </div>
  );
}
